import {type AppBskyActorDefs} from '@atproto/api'
import {useQuery} from '@tanstack/react-query'

import {COMMUNITY_PDS_BASE_URLS} from '#/lib/constants'
import {logger} from '#/logger'
import {STALE} from '#/state/queries'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'

type LocalAccount = {did: string; handle: string}

// app.bsky.actor.getProfiles caps `actors` at 25 per call.
const GET_PROFILES_BATCH_SIZE = 25

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

/**
 * Profiles hydrated via `getProfiles` come back as `ProfileViewDetailed`,
 * which carries a `$type` literal that's incompatible with the narrower
 * `ProfileView`/`ProfileViewBasic` shapes those results get merged into
 * elsewhere. Stripping it makes the object structurally assignable to any of
 * the three profile view shapes.
 */
export type LocalProfile = Omit<AppBskyActorDefs.ProfileViewDetailed, '$type'>

async function fetchLocalAccountsDirectory(
  baseUrl: string,
): Promise<LocalAccount[]> {
  const res = await fetch(`${baseUrl}/community-search/local-accounts`)
  if (!res.ok) {
    throw new Error(`Failed to fetch local accounts directory (${res.status})`)
  }
  const json: {accounts?: LocalAccount[]} = await res.json()
  return json.accounts ?? []
}

export function matchesQuery(
  profile: {handle: string; displayName?: string},
  query: string,
): boolean {
  const q = query.toLowerCase().trim()
  if (!q) {
    return false
  }
  return (
    profile.handle.toLowerCase().includes(q) ||
    !!profile.displayName?.toLowerCase().includes(q)
  )
}

const RQKEY_ROOT = 'community-local-accounts'
export const RQKEY = () => createQueryKey(RQKEY_ROOT, {})

/**
 * The set of profiles hosted on a known ClearCommunity-network PDS,
 * regardless of how the federated search ranks them. Used to guarantee local
 * accounts surface in search even when they're too low-follower to make the
 * federated AppView's relevance-ranked result page.
 */
export function useLocalAccountsQuery() {
  const agent = useAgent()
  return useQuery<LocalProfile[]>({
    queryKey: RQKEY(),
    staleTime: STALE.MINUTES.FIVE,
    queryFn: async () => {
      try {
        const results = await Promise.allSettled(
          COMMUNITY_PDS_BASE_URLS.map(fetchLocalAccountsDirectory),
        )
        const dids = new Set<string>()
        for (const result of results) {
          if (result.status === 'fulfilled') {
            for (const account of result.value) {
              dids.add(account.did)
            }
          }
        }
        if (dids.size === 0) {
          return []
        }
        const batches = chunk([...dids], GET_PROFILES_BATCH_SIZE)
        const batchResults = await Promise.all(
          batches.map(actors => agent.getProfiles({actors})),
        )
        return batchResults
          .flatMap(res => res.data.profiles)
          .map(({$type: _type, ...profile}) => profile)
      } catch (e) {
        logger.error(
          'useLocalAccountsQuery: failed to hydrate local accounts',
          {
            message: e,
          },
        )
        return []
      }
    },
  })
}
